/**
 * Les échéances d'une EURL à l'IS ou d'une SASU, sur les douze mois qui
 * viennent, datées au jour près.
 *
 * TOUT SE DÉDUIT DE LA DATE DE CLÔTURE. L'impôt sur les sociétés, la liasse,
 * l'approbation des comptes, leur dépôt, la limite de versement des dividendes :
 * chacun se compte depuis la fin de l'exercice. Le lecteur donne la date de sa
 * prochaine clôture, et le module calcule les échéances de l'exercice qui se
 * termine ET de celui qui vient de se terminer, parce qu'en septembre une
 * société qui clôt au 30 juin a encore sa liasse devant elle.
 *
 * LES DATES QUI TOMBENT UN WEEK-END. Pour les paiements, l'administration
 * reporte au premier jour ouvré suivant : le calendrier fiscal 2026 le fait
 * pour l'acompte du 15 mars (payé le 16), et le BOFiP l'écrit
 * (BOI-REC-PRO-10-10-20 § 60). Le module applique la même règle aux paiements.
 *
 * CE QUE LE MODULE NE DATE PAS, ET POURQUOI (relevé du 24 septembre 2026) :
 *   - la TVA des sociétés au régime simplifié en 2027 : ce régime disparaît au
 *     1er janvier 2027 (loi n° 2025-127, article 38), les déclarations
 *     deviennent trimestrielles, et leurs dates ne sont pas publiées ;
 *   - le jour exact d'une déclaration de TVA : il dépend du département et du
 *     SIREN, et l'espace professionnel le donne ; on écrit la fourchette ;
 *   - la déclaration des revenus d'un gérant en 2027 : ses dates changent
 *     chaque printemps.
 * Au-delà de 2026, les dates sont CALCULÉES selon la règle, et marquées comme
 * telles : le calendrier fiscal 2027 n'est pas encore paru.
 */

import { ajouterJours, iso, jourUtc, nemeJourOuvreApres, ouvreOuSuivant } from "./workingDays.js";

/** Le seuil d'impôt de référence en deçà duquel aucun acompte n'est dû. */
export const SEUIL_ACOMPTES_IS = 3000;

/** Au-delà de cette année, les dates sont calculées, pas publiées. */
export const DERNIERE_ANNEE_PUBLIEE = 2026;

/** Le même jour, `mois` mois plus tard ; ou le dernier jour du mois s'il n'existe pas. */
function moisPlusTard(date, mois) {
    const cible = jourUtc(date.getUTCFullYear(), date.getUTCMonth() + mois, 1);
    const dernier = jourUtc(cible.getUTCFullYear(), cible.getUTCMonth() + 1, 0).getUTCDate();
    // Une clôture en fin de mois donne une échéance en fin de mois.
    const finDeMois = jourUtc(date.getUTCFullYear(), date.getUTCMonth() + 1, 0).getUTCDate() === date.getUTCDate();
    return jourUtc(cible.getUTCFullYear(), cible.getUTCMonth(), finDeMois ? dernier : Math.min(date.getUTCDate(), dernier));
}

/** Le 15 du `mois`-ième mois qui suit la date. */
function quinzeDuMois(date, mois) {
    return jourUtc(date.getUTCFullYear(), date.getUTCMonth() + mois, 15);
}

/**
 * Les échéances qui suivent la clôture d'un exercice.
 *
 * @param cloture  date de clôture (Date UTC à midi)
 */
export function echeancesDeCloture(cloture) {
    const auTrenteEtUn = cloture.getUTCMonth() === 11 && cloture.getUTCDate() === 31;
    // Liasse : le 2e jour ouvré après le 1er mai pour une clôture au
    // 31 décembre, sinon trois mois après la clôture ; plus quinze jours pour
    // les télédéclarants, c'est-à-dire toutes les sociétés à l'IS.
    const liasseLegale = auTrenteEtUn
        ? nemeJourOuvreApres(jourUtc(cloture.getUTCFullYear() + 1, 4, 1), 2)
        : moisPlusTard(cloture, 3);
    const approbation = moisPlusTard(cloture, 6);
    return {
        liasseLegale,
        liasse: ajouterJours(liasseLegale, 15),
        // Solde : le 15 du 4e mois qui suit la clôture ; mais pour une clôture
        // au 31 décembre, la loi dit le 15 mai, et non le 15 avril que la
        // règle générale donnerait (CGI, article 1668, 2). Paiement reporté au
        // jour ouvré suivant.
        solde: ouvreOuSuivant(auTrenteEtUn ? jourUtc(cloture.getUTCFullYear() + 1, 4, 15) : quinzeDuMois(cloture, 4)),
        approbation,
        // Dépôt : un mois après l'approbation, deux en ligne. On le date à
        // partir d'une approbation au dernier jour permis.
        depot: moisPlusTard(approbation, 2),
        // Dividendes : mis en paiement au plus tard neuf mois après la clôture.
        dividendes: moisPlusTard(cloture, 9),
    };
}

/**
 * Toutes les échéances des douze mois qui viennent.
 *
 * @param forme            « eurl » (à l'IS) ou « sasu »
 * @param prochaineCloture ISO de la clôture de l'exercice en cours
 * @param premierExercice  aucun exercice encore clos
 * @param dateCreation     ISO, pour la CFE de l'année de création
 * @param isReference      « plus », « moins » ou « inconnu » : l'impôt du
 *                         dernier exercice dépassait-il 3 000 € ?
 * @param tva              « franchise », « simplifie » ou « normal »
 * @param salaire          SASU : le président se verse un salaire
 * @param cotisations      EURL : « 5 », « 20 » ou « trimestre »
 * @param dividendes       l'associé compte se verser des dividendes
 * @param aujourdhui       ISO du jour de lecture
 */
export function echeancesSociete({
    forme,
    prochaineCloture,
    premierExercice = false,
    dateCreation,
    isReference = "inconnu",
    tva = "franchise",
    salaire = false,
    cotisations = "5",
    dividendes = false,
    aujourdhui,
}) {
    const debut = new Date(`${String(aujourdhui).slice(0, 10)}T12:00:00Z`);
    const fin = moisPlusTard(debut, 12);
    const dansLHorizon = (d) => d >= debut && d <= fin;
    const ponctuelles = [];
    const ajouter = (cle, date, extra = {}) => {
        if (!dansLHorizon(date)) return;
        ponctuelles.push({ cle, date, calculee: date.getUTCFullYear() > DERNIERE_ANNEE_PUBLIEE, ...extra });
    };

    const cloture = new Date(`${prochaineCloture}T12:00:00Z`);
    // L'exercice qui vient de se terminer, et celui qui se termine.
    const clotures = premierExercice
        ? [cloture]
        : [jourUtc(cloture.getUTCFullYear() - 1, cloture.getUTCMonth(), cloture.getUTCDate()), cloture];

    for (const c of clotures) {
        const e = echeancesDeCloture(c);
        ajouter("liasse", e.liasse, { legale: e.liasseLegale, cloture: c });
        ajouter("solde", e.solde, { cloture: c });
        ajouter("approbation", e.approbation, { cloture: c });
        ajouter("depot", e.depot, { cloture: c });
        if (dividendes) ajouter("dividendesLimite", e.dividendes, { cloture: c });
    }

    // Acomptes : les 15 mars, juin, septembre et décembre, pas pendant le
    // premier exercice, et pas si l'impôt de référence ne dépasse pas 3 000 €.
    if (!premierExercice && isReference !== "moins") {
        for (let annee = debut.getUTCFullYear(); annee <= fin.getUTCFullYear(); annee++) {
            for (const mois of [2, 5, 8, 11]) {
                ajouter("acompteIs", ouvreOuSuivant(jourUtc(annee, mois, 15)), { incertain: isReference === "inconnu" });
            }
        }
    }

    // CFE : le solde au 15 décembre, sauf l'année de création, exonérée ; la
    // déclaration initiale 1447-C au 31 décembre de cette même année.
    const anneeCreation = dateCreation ? Number(String(dateCreation).slice(0, 4)) : null;
    for (let annee = debut.getUTCFullYear(); annee <= fin.getUTCFullYear(); annee++) {
        if (anneeCreation !== null && annee === anneeCreation) {
            ajouter("cfeDeclaration", jourUtc(annee, 11, 31));
        } else if (anneeCreation === null || annee > anneeCreation) {
            ajouter("cfe", ouvreOuSuivant(jourUtc(annee, 11, 15)));
        }
    }

    // TVA au régime simplifié : l'acompte de décembre 2026 est le dernier ;
    // en 2027, le régime disparaît.
    if (tva === "simplifie") {
        ajouter("tvaAcompteDecembre", jourUtc(2026, 11, 24), { fourchette: [15, 24] });
    }

    // Dividendes : l'IFU au 15 février qui suit l'année du versement, et la
    // demande de dispense d'acompte au 30 novembre, pour l'année suivante.
    if (dividendes) {
        for (let annee = debut.getUTCFullYear(); annee <= fin.getUTCFullYear(); annee++) {
            ajouter("ifu", ouvreOuSuivant(jourUtc(annee, 1, 15)));
            ajouter("dispense", jourUtc(annee, 10, 30));
        }
    }

    ponctuelles.sort((a, b) => a.date - b.date);

    // Ce qui revient chaque mois ou chaque trimestre.
    const recurrentes = [];
    if (forme === "sasu" && salaire) recurrentes.push({ cle: "dsn", jour: 15 });
    if (forme === "eurl") {
        recurrentes.push(
            cotisations === "trimestre" ? { cle: "cotisationsTrimestre" } : { cle: "cotisationsMois", jour: Number(cotisations) },
        );
    }
    if (tva === "normal") recurrentes.push({ cle: "ca3", fourchette: [15, 24] });
    if (dividendes) recurrentes.push({ cle: "formulaire2777" });

    // Ce qui ne se date pas, mais qu'il faut savoir.
    const informations = [];
    if (premierExercice) informations.push("premierExercice");
    if (tva === "simplifie") informations.push("tva2027");
    if (tva === "franchise") informations.push("tvaFranchise");
    if (forme === "eurl") informations.push("declarationRevenus");
    if (forme === "sasu" && !salaire) informations.push("sansSalaire");

    return { ponctuelles, recurrentes, informations, debut: iso(debut), fin: iso(fin) };
}
